import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone, Apple, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export function PWAInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detectar iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Detectar se já está em modo standalone (instalado)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Verificar se já foi dispensado
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Não mostrar se já está instalado ou foi dispensado há menos de 7 dias
    if (standalone || (dismissed && daysSinceDismissed < 7)) {
      return;
    }

    // Listener para o evento beforeinstallprompt (Chrome/Edge/Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Para iOS, mostrar banner após 3 segundos
    if (iOS && !standalone) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="container max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 bg-white/20 p-3 rounded-full">
          {isIOS ? (
            <Apple className="h-8 w-8" />
          ) : (
            <Smartphone className="h-8 w-8" />
          )}
        </div>

        {/* Text */}
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-bold text-lg">Instale o App U-Clean!</h3>
          {isIOS ? (
            <p className="text-sm text-white/90">
              Toque em <span className="inline-flex items-center mx-1 bg-white/20 px-2 py-0.5 rounded">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 16L12 3M12 3L7 8M12 3L17 8M3 16V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </span> e depois <strong>"Adicionar à Tela de Início"</strong>
            </p>
          ) : (
            <p className="text-sm text-white/90">
              Acesse mais rápido, funciona offline e receba notificações!
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isIOS && deferredPrompt && (
            <Button 
              onClick={handleInstall}
              className="bg-white text-green-700 hover:bg-green-50 font-bold"
              data-testid="pwa-install-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Instalar Agora
            </Button>
          )}
          <Link to="/instalar">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 text-xs"
              data-testid="pwa-help-btn"
            >
              <HelpCircle className="mr-1 h-4 w-4" />
              Ajuda
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="text-white hover:bg-white/20"
            data-testid="pwa-dismiss-btn"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {isIOS && (
        <div className="mt-4 bg-white/10 p-4 rounded-lg max-w-md mx-auto">
          <p className="text-sm font-medium mb-2">Instruções para iPhone/iPad:</p>
          <ol className="text-sm space-y-2">
            <li className="flex items-start gap-2">
              <span className="bg-white/30 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
              <span>Toque no ícone de compartilhar (quadrado com seta para cima)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-white/30 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
              <span>Role para baixo e toque em "Adicionar à Tela de Início"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-white/30 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
              <span>Toque em "Adicionar" no canto superior direito</span>
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
